import type { FlowSummary } from '../types';

export const NO_NAMESPACE = '';

/** `personio/attendance-event` → `personio`; names without a slash have no namespace. */
export function namespaceOf(name: string): string {
  const slash = name.indexOf('/');
  return slash > 0 ? name.slice(0, slash).trim() : NO_NAMESPACE;
}

export interface FlowGroup {
  key: string;
  flows: FlowSummary[];
}

/** Groups by namespace, case-insensitively, alphabetical; flows without one come last. */
export function groupByNamespace(flows: FlowSummary[]): FlowGroup[] {
  const groups = new Map<string, FlowGroup>();
  for (const flow of flows) {
    const ns = namespaceOf(flow.name);
    const id = ns.toLowerCase();
    if (!groups.has(id)) groups.set(id, { key: ns, flows: [] });
    groups.get(id)!.flows.push(flow);
  }
  return [...groups.values()].sort((a, b) => {
    if (a.key === NO_NAMESPACE) return 1;
    if (b.key === NO_NAMESPACE) return -1;
    return a.key.localeCompare(b.key, undefined, { sensitivity: 'base' });
  });
}

export interface CallTreeNode {
  flow: FlowSummary;
  /** Unique per position in the tree; a flow with two callers appears twice. */
  path: string;
  children: CallTreeNode[];
  /** Other flows that also call this one, besides the parent it is shown under. */
  otherCallerIds: string[];
}

export function callersById(flows: FlowSummary[]): Map<string, string[]> {
  const known = new Set(flows.map(f => f.id));
  const callers = new Map<string, string[]>();
  for (const flow of flows) {
    for (const target of flow.calledFlowIds) {
      if (target === flow.id || !known.has(target)) continue;
      if (!callers.has(target)) callers.set(target, []);
      callers.get(target)!.push(flow.id);
    }
  }
  return callers;
}

/**
 * Roots are flows nobody calls; each flow's sub-flows hang beneath it. A flow called from
 * several places appears under each caller. A call cycle stops at the first repeat, and a
 * cycle no root reaches is shown from its first member so no flow goes missing.
 */
export function buildCallTree(
  flows: FlowSummary[],
  compare: (a: FlowSummary, b: FlowSummary) => number,
): CallTreeNode[] {
  const byId = new Map(flows.map(f => [f.id, f]));
  const callers = callersById(flows);
  const placed = new Set<string>();

  function build(flow: FlowSummary, parentId: string | null, ancestors: Set<string>, path: string): CallTreeNode {
    placed.add(flow.id);
    const nextAncestors = new Set(ancestors).add(flow.id);
    const children = [...new Set(flow.calledFlowIds)]
      .filter(id => id !== flow.id && !nextAncestors.has(id))
      .map(id => byId.get(id))
      .filter((f): f is FlowSummary => f !== undefined)
      .sort(compare)
      .map(child => build(child, flow.id, nextAncestors, `${path}/${child.id}`));
    return {
      flow,
      path,
      children,
      otherCallerIds: (callers.get(flow.id) ?? []).filter(id => id !== parentId),
    };
  }

  const sorted = [...flows].sort(compare);
  const roots = sorted
    .filter(f => !callers.has(f.id))
    .map(f => build(f, null, new Set(), f.id));
  for (const flow of sorted) {
    if (!placed.has(flow.id)) roots.push(build(flow, null, new Set(), flow.id));
  }
  return roots;
}

/** Keeps every node that matches or has a matching descendant, so a match keeps its context. */
export function filterCallTree(
  nodes: CallTreeNode[],
  matches: (flow: FlowSummary) => boolean,
): CallTreeNode[] {
  return nodes.flatMap(node => {
    const children = filterCallTree(node.children, matches);
    return matches(node.flow) || children.length > 0 ? [{ ...node, children }] : [];
  });
}
